import { useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ColumnFiltersState } from "@tanstack/react-table";

// URL format: columnId.operator=value
// e.g. ?batchId.equals=abc&orderDate.after=2024-01-01&orderDate.before=2024-12-31

function filtersToParams(filters: ColumnFiltersState): URLSearchParams {
  const params = new URLSearchParams();
  for (const f of filters) {
    const { id, value } = f as any;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v.value) params.set(`${id}.${v.operator}`, v.value);
      }
    } else if (value?.value) {
      params.set(`${id}.${value.operator}`, value.value);
    }
  }
  return params;
}

function paramsToFilters(params: URLSearchParams): ColumnFiltersState {
  const map: Record<string, any[]> = {};
  params.forEach((val, key) => {
    const dotIdx = key.lastIndexOf(".");
    if (dotIdx === -1) return;
    const id = key.slice(0, dotIdx);
    const operator = key.slice(dotIdx + 1);
    if (!map[id]) map[id] = [];
    map[id].push({ operator, value: val });
  });

  const filters: ColumnFiltersState = [];
  for (const [id, entries] of Object.entries(map)) {
    const isDate = entries.some((e) =>
      ["after", "before"].includes(e.operator),
    );
    if (isDate) {
      const dateFilters = [
        { operator: "after", value: "", type: "date" },
        { operator: "before", value: "", type: "date" },
      ];
      for (const e of entries) {
        const match = dateFilters.find((d) => d.operator === e.operator);
        if (match) match.value = e.value;
      }
      filters.push({ id, value: dateFilters });
    } else {
      const e = entries[0];
      filters.push({
        id,
        value: { operator: e.operator, value: e.value, type: "text" },
      });
    }
  }
  return filters;
}

export function useFilterParams(
  localStorageKey: string,
  onUrlChange?: (filters: ColumnFiltersState) => void,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastUrlString = useRef(searchParams.toString());

  const getInitialFilters = (): ColumnFiltersState => {
    const urlFilters = paramsToFilters(searchParams);
    if (urlFilters.length > 0) return urlFilters;
    return JSON.parse(localStorage.getItem(localStorageKey) || "[]");
  };

  const syncToUrl = (filters: ColumnFiltersState) => {
    const params = filtersToParams(filters);
    const paramsString = params.toString();
    if (paramsString !== lastUrlString.current) {
      lastUrlString.current = paramsString;
      setSearchParams(params, { replace: true });
    }
  };

  // Watch for URL changes (e.g. clicking a link) and push them into filters
  useEffect(() => {
    const currentString = searchParams.toString();
    if (currentString === lastUrlString.current) return;
    lastUrlString.current = currentString;

    if (onUrlChange) {
      const urlFilters = paramsToFilters(searchParams);
      if (urlFilters.length > 0) {
        onUrlChange(urlFilters);
      } else {
        onUrlChange(JSON.parse(localStorage.getItem(localStorageKey) || "[]"));
      }
    }
  }, [searchParams]);

  return { getInitialFilters, syncToUrl };
}
